import { type DocNode } from '@atlaskit/adf-schema';
import type { CorePlugin, PublicPluginAPI, SelectionContext } from '@atlaskit/editor-common/types';
import { type JSONNode } from '@atlaskit/editor-json-transformer';
import type { UserIntentPlugin } from '@atlaskit/editor-plugin-user-intent';
import type { RemixFeedbackMetadata } from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import type { InteractionSource } from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import type { StoreChatItem } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import type { StoreAssistant } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import type { ConversationAssistantConfig } from '@atlassian/conversation-assistant/types';
import type { SmartCreationContext } from '@atlassian/conversation-assistant/ui/staging-area-modal';

import type { RemixHiddenOptionKind } from '../../types';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports -- RemixOptionKind is the canonical type owned by the sibling remix-overlay module; duplicating it here would drift.
import type { RemixOptionKind } from '../remix/remix-overlay';

import type { ContentClassification } from './utils/content-classification';
export type { ContentClassification } from './utils/content-classification';

export type EditorApiWithCorePlugin = PublicPluginAPI<[CorePlugin, UserIntentPlugin]>;

export type DynamicUiSource = {
	contentType: string;
	endIndex: number;
	endLocalId: string;
	fragmentAdf: JSONNode[] | null;
	fullAdf: DocNode;
	startIndex: number;
	startLocalId: string;
};

export type RemixModalMetadataLike = {
	[key: string]: unknown;
	aspectRatio?: string | null;
	imageStyle?: string | null;
	dynamicUiType?: RemixOptionKind;
	interactionSource?: InteractionSource;
	userPrompt?: string;
	onCancel?: () => void;
	aiFeatureName?: string;
};

export type AddRemixHistoryEntryPayload = {
	conversationId: string;
	dynamicUiSource: DynamicUiSource;
	dynamicUiSubtype?: string;
	dynamicUiType: string;
	finalMessage: StoreAssistant;
	/** Classification captured from ACTION_CLASSIFICATION plus any content-specific stream data. */
	contentClassification?: ContentClassification;
	/** for image, there should be image fieldId */
	fieldId?: string;
	/** for MAUI extensions, there should be MAUI url */
	url?: string;
};

export type RemixHistoryEntry = {
	conversationId: string;
	dynamicUiSource: DynamicUiSource;
	latestFinalMessage: StoreAssistant;
	latestFinalMessageId?: string;
	stack: Array<{
		dynamicUiSubtype?: string;
		dynamicUiType: string;
		fieldId?: string;
		finalMessage: StoreAssistant;
		finalMessageId?: string;
		url?: string;
	}>;
};

export type RemixHistoryEntryForUpdate = {
	conversationId: string;
	dynamicUiSource: DynamicUiSource;
	latestFinalMessage?: StoreAssistant;
	latestFinalMessageId?: string;
	dynamicUiSubtype?: string;
	dynamicUiType: string;
	fieldId?: string;
	finalMessage: StoreAssistant;
	finalMessageId?: string;
	url?: string;
};

export type UpdateRemixImageHistoryFieldIdPayload = {
	conversationId: string;
	fieldId: string;
};

export type AddRemixHistoryStackEntryPayload = {
	conversationId: string;
	dynamicUiType: string;
	dynamicUiSubtype?: string;
	fieldId?: string;
	finalMessage: StoreAssistant;
	previousFieldId?: string;
	previousUrl?: string;
	url?: string;
};

/**
 * Dynamic-ui (MAUI) variant of {@link AddBackendRemixHistoryStackEntryPayload}.
 * Used when the bridge stream regenerates a MAUI app on the page.
 */
export type AddBackendMauiRemixHistoryStackEntryPayload = {
	/** The MAUI app id being replaced. */
	previousMauiId: string;
	/** New MAUI app URL synthesized from the BE-side new app id. */
	url: string;
	/** Inherits from the matched stack entry when omitted. */
	dynamicUiType?: string;
};

/**
 * Image variant of {@link AddBackendRemixHistoryStackEntryPayload}.
 * Used when the bridge stream regenerates an image on the page.
 */
export type AddBackendImageRemixHistoryStackEntryPayload = {
	/** The media file id being replaced (BE-side previousMediaFileId). */
	previousFieldId: string;
	/** Live upload-side file id from onUploadAndInsertMedia's result. */
	fieldId: string;
};

/**
 * Payload for appending a stack entry from a backend-initiated remix update
 * (bridge stream). The reducer locates the existing entry by matching the
 * previous identifier against stack entries' MAUI app id (extracted from url)
 * or fieldId, then inherits `finalMessage`, `finalMessageId`, `dynamicUiSubtype`,
 * and (when omitted) `dynamicUiType` from the matched entry.
 *
 * `dynamicUiSubtype` is intentionally NOT part of the payload: a backend edit
 * (e.g. "Ask Rovo") does not receive the current subtype, so its echoed value
 * is unreliable (it defaults, e.g. quadrant comes back as 'flip-card'). The
 * subtype is unchanged by such edits, so the reducer always inherits it from
 * the matched stack entry instead.
 *
 * Discriminated union of the dynamic-ui (MAUI) and image variants;
 * narrow with {@link isAddBackendMauiRemixHistoryStackEntryPayload}.
 */
export type AddBackendRemixHistoryStackEntryPayload =
	| AddBackendMauiRemixHistoryStackEntryPayload
	| AddBackendImageRemixHistoryStackEntryPayload;

/**
 * Type guard discriminating the dynamic-ui (MAUI) variant of
 * {@link AddBackendRemixHistoryStackEntryPayload} from the image variant.
 * The two branches have disjoint required fields (`previousMauiId` vs
 * `previousFieldId`), so an `in`-check on either is a safe discriminator.
 */
export function isAddBackendMauiRemixHistoryStackEntryPayload(
	payload: AddBackendRemixHistoryStackEntryPayload,
): payload is AddBackendMauiRemixHistoryStackEntryPayload {
	return 'previousMauiId' in payload;
}

export type RemixContentComponentProps<
	TEditorApi extends EditorApiWithCorePlugin = EditorApiWithCorePlugin,
> = {
	addHistoryEntry?: (options: AddRemixHistoryEntryPayload) => void;
	addHistoryStackEntry?: (options: AddRemixHistoryStackEntryPayload) => void;
	/** See `EditorRemixContext.onVariantUpdateComplete`. */
	onVariantUpdateComplete?: (id: string) => void;
	clearThinkingDecoration?: () => void;
	replaceWhiteboardEmbed?: (options: {
		contentId: string;
		finalizedUrl: string;
		localId?: string;
	}) => void;
	setMauiEmbedFinalizeStatus?: (options: {
		contentId: string;
		status: 'suppressed' | 'needs-finalize';
	}) => void;
	editorApi?: TEditorApi;
	handleModalClose: () => void;
	hiddenOptions?: readonly RemixHiddenOptionKind[];
	isComplete?: boolean;
	isModalOpen: boolean;
	isThinking: boolean;
	isUpdate?: boolean;
	isViewMode?: boolean;
	historyEntryForUpdate?: RemixHistoryEntryForUpdate;
	dynamicUiSubtypeForUpdate?: string;
	showLocationPicker?: boolean;
	setThinkingComplete?: (isComplete: boolean) => void;
	finishRemixThinking?: () => void;
	onRemixThinkingCompleteAnimationFinished?: () => void;
	onStartThinking: (options?: { contentType?: string }) => void;
	onDismissThinkingUi?: (options?: { reopenModal?: boolean }) => void;
	onStopThinking: (options?: { reopenModal?: boolean }) => void;
	onRegisterAbortRequest?: (abortFn: () => void) => () => void;
	setShowLocationPicker?: (showLocationPicker: boolean) => void;
	remixModalMetadata?: RemixModalMetadataLike;
	remixSource?: string;
	selectedContext: SelectionContext;
};

export type RemixContentComponentFullProps<
	TEditorApi extends EditorApiWithCorePlugin = EditorApiWithCorePlugin,
> = RemixContentComponentProps<TEditorApi> & {
	entryPoint?: string;
	productConfig: RemixProductConfig;
	productIntegration: RemixProductIntegration;
	smartCreationConfig?: Pick<ConversationAssistantConfig, 'siteId' | 'userId' | 'product'>;
	smartCreationContext?: SmartCreationContext;
};

export type OpenRemixPreviewOptions = {
	isFloating: boolean;
	remixConversationId?: string;
};

export type RemixInsertSuccessPayload = {
	currentConversationId?: string;
	remixMetadata?: RemixFeedbackMetadata;
};

export type RemixProductIntegration = {
	closeRemixPreview: () => void;
	contentType: string;
	isRemixPreviewVisible: boolean;
	onRemixInsertSuccess?: (payload: RemixInsertSuccessPayload) => void;
	openRemixPreview: (options: OpenRemixPreviewOptions) => void;
	toggleConversationAssistant?: (location: string) => void;
};

export type RemixProductConfig = {
	analyticsProductName?: string;
	isActionReady?: (chatMessages: StoreChatItem[]) => boolean;
	productKey: string;
	shouldUseEphemeralPreview?: (chatMessages: StoreChatItem[]) => boolean;
};
